<script setup>
// ============================================================================
// BonBoZ: TAG THEO TRANG — màn hình cài đặt riêng.
//
// Trước đây phần cấu hình này là một modal mở ngay từ lưới tag dưới khung chat.
// Sai chỗ: trực page gắn tag hàng trăm lần mỗi ngày, còn khai bộ tag là việc
// quản trị làm một lần rồi thôi — để lẫn vào khung chat vừa chật vừa dễ bấm
// nhầm. Đưa về Cài đặt, đúng nơi mọi cấu hình khác của Chatwoot nằm.
//
// Label của Chatwoot thuộc CẢ TÀI KHOẢN; phần "tag nào thuộc trang nào" là ánh
// xạ riêng của ta, lưu trong cấu hình theo trang (khoá `labels`). Trang chưa
// cấu hình thì lưới hiện tất cả tag — đội nhỏ không khai gì vẫn dùng được.
//
// Dựng bằng đúng bộ khung Chatwoot đã có (SettingsLayout / BaseSettingsHeader /
// BaseTable / Button / woot-modal): import và sắp xếp, không dựng lại lõi.
// ============================================================================
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import {
  loadPageConfigs,
  setPageConfig,
  pageLabelsOf,
  loiNap,
} from 'dashboard/helper/pageConfig';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import LabelPicker from './LabelPicker.vue';
import BulkCreate from './BulkCreate.vue';

const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const accountLabels = useMapGetter('labels/getLabels');

const dangTai = ref(true);
const timKiem = ref('');
const trangDangChon = ref(null);
const moTaoHangLoat = ref(false);

// Nạp hỏng thì màn hình này vô dụng, nhưng phải nói RÕ vì sao — và trấn an
// rằng gắn tag trong khung chat không hỏng theo. Kèm lý do thật từ server:
// nuốt hết vào một câu thì "không có quyền" và "mạng chết" trông y hệt nhau.
const loi = computed(() =>
  loiNap.value ? `${t('BONBOZ.TAG_TRANG.SERVICE_DOWN')} (${loiNap.value})` : ''
);

const napTagTrang = async () => {
  // `force`: bản đồ có thể đã nạp từ màn Hội thoại, ở đây cần bản mới nhất.
  await loadPageConfigs(true);
  dangTai.value = false;
};

onMounted(() => {
  if (!inboxes.value.length) store.dispatch('inboxes/get');
  if (!accountLabels.value.length) store.dispatch('labels/get');
  napTagTrang();
});

const tagCuaTrang = pageLabelsOf;

const mauTag = title =>
  accountLabels.value.find(l => l.title === title)?.color || '#94A3B8';

const danhSach = computed(() => {
  const q = timKiem.value.trim();
  if (!q) return inboxes.value;
  return picoSearch(inboxes.value, q, [{ name: 'name', weight: 4 }]);
});

const tieuDeCot = computed(() => [
  t('BONBOZ.TAG_TRANG.TABLE_PAGE'),
  t('BONBOZ.TAG_TRANG.TABLE_TAGS'),
  t('BONBOZ.TAG_TRANG.TABLE_ACTION'),
]);

// Cấu hình server trả về sau khi gộp — nhét thẳng vào bản đồ để bảng đổi ngay.
const daLuu = ({ inboxId, cauHinh }) => {
  setPageConfig(inboxId, cauHinh);
  trangDangChon.value = null;
};

const daTao = () => {
  // Không nạp lại: BulkCreate đã nhét cấu hình server trả về vào bản đồ, còn
  // label mới thì store `labels` tự cập nhật lúc tạo.
  moTaoHangLoat.value = false;
};
</script>

<template>
  <SettingsLayout
    :is-loading="dangTai"
    :loading-message="$t('BONBOZ.TAG_TRANG.LOADING')"
    :no-records-found="!inboxes.length"
    :no-records-message="$t('BONBOZ.TAG_TRANG.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="timKiem"
        :title="$t('BONBOZ.TAG_TRANG.HEADER')"
        :description="$t('BONBOZ.TAG_TRANG.DESCRIPTION')"
        :search-placeholder="$t('BONBOZ.TAG_TRANG.SEARCH_PLACEHOLDER')"
      >
        <template v-if="inboxes.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('BONBOZ.TAG_TRANG.COUNT', { n: inboxes.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('BONBOZ.TAG_TRANG.BULK_BTN')"
            size="sm"
            @click="moTaoHangLoat = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-3">
        <p v-if="loi" class="m-0 text-sm text-n-ruby-11">{{ loi }}</p>

        <BaseTable
          :headers="tieuDeCot"
          :items="danhSach"
          :no-data-message="$t('BONBOZ.TAG_TRANG.EMPTY')"
        >
          <template #row="{ items }">
            <BaseTableRow v-for="hop in items" :key="hop.id" :item="hop">
              <template #default>
                <BaseTableCell>
                  <div class="flex items-center gap-2">
                    <Avatar
                      :src="hop.avatar_url"
                      :name="hop.name"
                      :size="24"
                      rounded-full
                    />
                    <span class="text-body-main text-n-slate-12">
                      {{ hop.name }}
                    </span>
                  </div>
                </BaseTableCell>

                <BaseTableCell>
                  <!-- Hiện luôn tag để nhìn phát biết trang nào khai gì, khỏi
                       phải mở từng modal ra xem. -->
                  <div
                    v-if="tagCuaTrang(hop.id).length"
                    class="flex flex-wrap gap-1"
                  >
                    <span
                      v-for="title in tagCuaTrang(hop.id).slice(0, 6)"
                      :key="title"
                      class="rounded px-1.5 text-xs leading-5 text-n-slate-12"
                      :style="{ backgroundColor: `${mauTag(title)}55` }"
                    >
                      {{ title }}
                    </span>
                    <span
                      v-if="tagCuaTrang(hop.id).length > 6"
                      class="text-xs leading-5 text-n-slate-11"
                    >
                      {{
                        $t('BONBOZ.TAG_TRANG.MORE', {
                          n: tagCuaTrang(hop.id).length - 6,
                        })
                      }}
                    </span>
                  </div>
                  <span v-else class="text-body-main text-n-slate-11">
                    {{ $t('BONBOZ.TAG_TRANG.ALL') }}
                  </span>
                </BaseTableCell>

                <BaseTableCell align="end">
                  <Button
                    :label="$t('BONBOZ.TAG_TRANG.CHOOSE')"
                    size="sm"
                    slate
                    faded
                    :disabled="!!loi"
                    @click="trangDangChon = hop"
                  />
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
      </div>
    </template>

    <LabelPicker
      v-if="trangDangChon"
      :inbox="trangDangChon"
      :da-chon="tagCuaTrang(trangDangChon.id)"
      @close="trangDangChon = null"
      @saved="daLuu"
    />
    <BulkCreate
      v-if="moTaoHangLoat"
      @close="moTaoHangLoat = false"
      @created="daTao"
    />
  </SettingsLayout>
</template>
